import { GridColumn } from '@progress/kendo-react-grid'
import { type ExtractAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { HStack } from '~/components/elements/HStack'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import {
  StyledDropDownButton,
  type StyledDropDownButtonProps,
} from '~/components/elements/StyledDropDownButton'
import { StyledGrid } from '~/components/elements/StyledGrid'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLanguageTextWidth } from '~/hooks/useLanguageTextWidth'

import { dialogInfoAtom } from '../atoms'
import { useCustomerAttributeGroupId } from '../hooks/useCustomerAttributeGroupId'
import { useCustomerAttributeItems } from '../hooks/useCustomerAttributeItems'
import { useSurveyId } from '../hooks/useSurveyId'
import { pathKey } from '../utils'

import { CommandsCell } from './CommandsCell'
import styles from './CustomerAttributeList.css'
import { Dialogs } from './Dialogs'

type DialogType = NonNullable<
  ExtractAtomValue<typeof dialogInfoAtom>
>['dialogType']
type CreateType = Extract<DialogType, 'create' | 'createInBulkSelectFilters3'>
type AddButtonItem = {
  label: string
  value: CreateType
}

export const CustomerAttributeList = () => {
  const customerAttributeGroupId = useCustomerAttributeGroupId()
  const surveyId = useSurveyId()
  const { t } = useTranslation()
  const createButtonItems = React.useMemo<AddButtonItem[]>(
    () => [
      { label: t('add_one_by_one'), value: 'create' },
      {
        label: t('add_in_batch'),
        value: 'createInBulkSelectFilters3',
      },
    ],
    [t],
  )

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('advancedAnalysis'),
      createBreadcrumb('customerAttributeAnalysis', { surveyId }),
      createBreadcrumb('customerAttributeSettings', { surveyId }),
      createBreadcrumb(pathKey, { customerAttributeGroupId, surveyId }),
    ],
    [createBreadcrumb, customerAttributeGroupId, surveyId],
  )

  const { customerAttributes, enabledIndices, groupName } =
    useCustomerAttributeItems()

  const isDisabledDeleteInBulkButton = enabledIndices.length === 0

  const setDialogInfo = useSetAtom(dialogInfoAtom)
  const handleCreateButtonItemClick = React.useCallback<
    EH<StyledDropDownButtonProps, 'onItemClick'>
  >(
    (event) => {
      const createType = event.item.value as CreateType
      setDialogInfo({ dialogType: createType })
    },
    [setDialogInfo],
  )
  const handleDeleteInBulkButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'deleteInBulkSelectCustomerAttributes' })
  }, [setDialogInfo])
  const handleSortButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'sort' })
  }, [setDialogInfo])

  const editButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 80 })
  const moveButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 90 })
  const copyButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 116 })
  const deleteButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 96 })

  const commandsCellWidth =
    12 +
    editButtonWidth +
    4 +
    moveButtonWidth +
    4 +
    copyButtonWidth +
    deleteButtonWidth +
    12

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack align="start" spacing={4} verticalAppearance="stretch">
        <Typography variant="heading1">
          {`${groupName} ${t('customized_segments_settings_3')}`}
        </Typography>

        <StyledGrid data={customerAttributes}>
          <GridColumn field="name" title={t('customized_segments_name')} />

          <GridColumn cell={CommandsCell} width={commandsCellWidth} />
        </StyledGrid>

        <HStack className={styles.buttons} justify="space-between" spacing={2}>
          <HStack spacing={2}>
            <StyledDropDownButton
              buttonStyle="success"
              icon="add"
              items={createButtonItems}
              text={t('add_segment')}
              textField="label"
              onItemClick={handleCreateButtonItemClick}
            />

            <StyledButton
              buttonStyle="success"
              icon="arrows-swap"
              onClick={handleSortButtonClick}
            >
              {t('sort')}
            </StyledButton>
          </HStack>

          <StyledButton
            buttonStyle="alert"
            disabled={isDisabledDeleteInBulkButton}
            icon="delete"
            onClick={handleDeleteInBulkButtonClick}
          >
            {t('select_and_delete_segments')}
          </StyledButton>
        </HStack>

        <Dialogs />
      </VStack>
    </PageContainer>
  )
}
